import React from 'react';
import { ArrowLeft, Shield, Lock, CheckCircle, FileCheck, Eye, Server, Key, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

const Security: React.FC = () => {
  const securityFeatures = [
    {
      icon: <Shield className="h-8 w-8 text-green-600" />,
      title: "PCI DSS Level 1 Compliant",
      description: "The highest level of payment card industry security certification. We undergo annual audits to maintain this standard."
    },
    {
      icon: <Lock className="h-8 w-8 text-blue-600" />,
      title: "End-to-End Encryption",
      description: "All payment data is encrypted using AES-256 encryption, the same standard used by banks and government agencies."
    },
    {
      icon: <FileCheck className="h-8 w-8 text-purple-600" />,
      title: "Valor Certified Installation",
      description: "All Valor devices are installed by certified technicians following Valor's strict security and compliance protocols."
    },
    {
      icon: <Eye className="h-8 w-8 text-orange-600" />,
      title: "24/7 Security Monitoring",
      description: "Our security operations center monitors all systems around the clock to detect and prevent threats in real-time."
    },
    {
      icon: <Server className="h-8 w-8 text-red-600" />,
      title: "Bank-Approved Devices",
      description: "All Valor devices we install are approved and trusted by major banks, ensuring the highest level of security and reliability."
    },
    {
      icon: <Key className="h-8 w-8 text-indigo-600" />,
      title: "Multi-Factor Authentication",
      description: "Optional MFA adds an extra layer of security to protect your account from unauthorized access."
    }
  ];


  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>
        
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="bg-green-100 p-4 rounded-full">
              <Shield className="h-16 w-16 text-green-600" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Security & Compliance</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Your data security is our top priority. We use bank-level encryption and industry-leading security practices to protect your information.
          </p>
        </div>

        {/* Security Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {securityFeatures.map((feature, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Valor & Bank Partnerships */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <div className="flex items-center gap-3 mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <h2 className="text-2xl font-bold text-gray-900">Valor Certified & Bank Trusted</h2>
          </div>
          <p className="text-gray-700 mb-6">
            We are Valor certified installers trusted by major banks including Chase, Bank of America, Wells Fargo, and other leading financial institutions. All Valor devices we install meet the highest security and compliance standards.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Valor Certification</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Certified Valor device installer</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Authorized to install all Valor POS systems</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Ongoing technical support certified</span>
                </li>
              </ul>
            </div>
            <div className="bg-green-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Bank Partnerships</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Trusted by major national banks</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Meets all bank security requirements</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Compliance verified by financial institutions</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Data Protection Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Encryption</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>AES-256 encryption</strong> for data at rest</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>TLS 1.3</strong> for data in transit</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Tokenization</strong> for sensitive payment data</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Regular security audits</strong> and penetration testing</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Controls</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Role-based access</strong> with granular permissions</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Multi-factor authentication</strong> (MFA) support</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Session management</strong> with automatic timeouts</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Activity logging</strong> for audit trails</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg shadow-lg p-8 mb-12">
          <div className="text-center">
            <Globe className="h-12 w-12 mx-auto mb-4 text-white" />
            <h2 className="text-3xl font-bold mb-4">Trusted by Businesses Nationwide</h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Over 500 businesses trust Simpl Processing with their payment processing. We process over $1 billion in transactions annually with zero security breaches.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">500+</div>
                <div className="text-gray-300">Businesses Trust Us</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">$1B+</div>
                <div className="text-gray-300">Processed Annually</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">99.9%</div>
                <div className="text-gray-300">Uptime Guarantee</div>
              </div>
            </div>
          </div>
        </div>

        {/* Incident Response */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Incident Response & Support</h2>
          <p className="text-gray-700 mb-4">
            In the unlikely event of a security incident, we have a comprehensive response plan in place:
          </p>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span><strong>24/7 Security Operations Center</strong> monitoring all systems</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span><strong>Automated threat detection</strong> and response systems</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span><strong>Regular security updates</strong> and patch management</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span><strong>Dedicated security team</strong> available for support</span>
            </li>
          </ul>
        </div>

        {/* Contact Section */}
        <div className="bg-blue-50 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions About Security?</h2>
          <p className="text-gray-700 mb-6">
            Our security team is available to answer any questions you may have about our security practices and compliance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:simran@simplprocessing.com" className="bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors inline-flex items-center justify-center gap-2">
              <span>Email Security Team</span>
            </a>
            <a href="tel:+1201-846-0591" className="bg-white text-gray-900 border-2 border-gray-900 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center justify-center gap-2">
              <span>Call (201) 846-0591</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Security;

